/**
 * @file DefineMappingTab.js
 * @description This file contains the DefineMappingTab component, which allows the user to define field mappings and manipulate data from the import file.
 */

import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ImportFieldDropdown from '../shared/asyncDropdowns/ImportFieldDropdown';
import styles from './ImportTemplates.module.scss';
import TranslationMapsAccordion from './TranslationMapsAccordion';
import Toast from '../ui/toasts/Toast';
import TranslateIcon from '../ui/svg/TranslateIcon';
import FormulaIcon from '../ui/svg/FormulaIcon';
import SideDrawer from '../helpers/SideDrawer';
import { useAuthContext, ModalContext, ModalTypes } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

/**
 * Builds a single mapper function's SideDrawer section from its reference details.
 */
const buildFunctionReferenceSection = ({
    name,
    description,
    syntax,
    notes,
}) => ({
    label: name,
    text: (
        <>
            {description}
            <br />
            {getText('msg_17c7ba7676ad')}: <code>{syntax}</code>
            {notes && (
                <>
                    <br />
                    {notes}
                </>
            )}
        </>
    ),
});

const MAPPER_FUNCTIONS_SECTIONS = [
    buildFunctionReferenceSection({
        name: 'Blank',
        description: getText('msg_6df5d309f603'),
        syntax: 'Blank()',
    }),
    buildFunctionReferenceSection({
        name: 'Concatenate',
        description: getText('msg_7ef6e41a0a04'),
        syntax: 'Concatenate(value1,value2,...)',
    }),
    buildFunctionReferenceSection({
        name: 'DateAdd',
        description: getText('msg_80f7f52b1b05'),
        syntax: 'DateAdd(interval,number,date)',
    }),
    buildFunctionReferenceSection({
        name: 'Day',
        description: getText('msg_91a8063c2c16'),
        syntax: 'Day(date)',
    }),
    buildFunctionReferenceSection({
        name: 'Days',
        description: getText('msg_a2b9174d3d27'),
        syntax: 'Days(date1,date2)',
    }),
    buildFunctionReferenceSection({
        name: 'EOMonth',
        description: getText('msg_b3ca285e4e38'),
        syntax: 'EOMonth(date)',
    }),
    buildFunctionReferenceSection({
        name: 'Fixed',
        description: getText('msg_c4db396f5f49'),
        syntax: 'Fixed(number,decimalPlaces)',
    }),
    buildFunctionReferenceSection({
        name: 'FOMonth',
        description: getText('msg_d5ec4a70805a'),
        syntax: 'FOMonth(date)',
    }),
    buildFunctionReferenceSection({
        name: 'Left',
        description: getText('msg_e6fd5b81916b'),
        syntax: 'Left(text,length)',
    }),
    buildFunctionReferenceSection({
        name: 'Len',
        description: getText('msg_f70e6c92a27c'),
        syntax: 'Len(text)',
    }),
    buildFunctionReferenceSection({
        name: 'Lower',
        description: getText('msg_081f7da3b38d'),
        syntax: 'Lower(text)',
    }),
    buildFunctionReferenceSection({
        name: 'Mid',
        description: getText('msg_19308eb4c49e'),
        syntax: 'Mid(text,start,length)',
    }),
    buildFunctionReferenceSection({
        name: 'Month',
        description: getText('msg_2a419fc5d5af'),
        syntax: 'Month(date)',
    }),
    buildFunctionReferenceSection({
        name: 'Proper',
        description: getText('msg_3b52b0d6e6c0'),
        syntax: 'Proper(text)',
    }),
    buildFunctionReferenceSection({
        name: 'Right',
        description: getText('msg_4c63c1e7f7d1'),
        syntax: 'Right(text,length)',
    }),
    buildFunctionReferenceSection({
        name: 'Search',
        description: getText('msg_5d74d2f808e2'),
        syntax: 'Search(searchFor,text)',
    }),
    buildFunctionReferenceSection({
        name: 'Substitute',
        description: getText('msg_6e85e30919f3'),
        syntax: 'Substitute(text,oldText,newText)',
    }),
    buildFunctionReferenceSection({
        name: 'TextSplit',
        description: getText('msg_7f96f41a2a04'),
        syntax: 'TextSplit(text,delimiter,itemNumber)',
    }),
    buildFunctionReferenceSection({
        name: 'Today',
        description: getText('msg_80a7052b3b15'),
        syntax: 'Today() or Today(offset)',
    }),
    buildFunctionReferenceSection({
        name: 'Upper',
        description: getText('msg_91b8163c4c26'),
        syntax: 'Upper(text)',
    }),
    buildFunctionReferenceSection({
        name: 'ValueOf',
        description: getText('msg_a2c9274d5d37'),
        syntax: 'ValueOf(fieldName)',
        notes: getText('msg_b3da385e6e48'),
    }),
    buildFunctionReferenceSection({
        name: 'Weekday',
        description: getText('msg_c4eb496f7f59'),
        syntax: 'Weekday(date)',
    }),
    buildFunctionReferenceSection({
        name: 'Year',
        description: getText('msg_d5fc5a80906a'),
        syntax: 'Year(date)',
    }),
    buildFunctionReferenceSection({
        name: 'ZeroFill',
        description: getText('msg_e60d6b91a17b'),
        syntax: 'ZeroFill(text,length)',
    }),
    {
        label: getText('msg_5cf4c2f8f502'),
        text: (
            <ul className="mb-0">
                <li>
                    {getText('msg_082f8db3c39d')}{' '}
                    <code>FunctionName(param1,param2,...)</code>.
                </li>
                <li>{getText('msg_19409ec4d4ae')}</li>
                <li>
                    {getText('msg_2a51afd5e5bf')}{' '}
                    <code>{'Concatenate("Smith, ")'}</code>.{' '}
                    {getText('msg_3b62c0e6f6d0')}
                </li>
                <li>
                    {getText('msg_4c73d1f707e1')}{' '}
                    <code>{'Concatenate("Hello ", ValueOf(First Name))'}</code>.
                </li>
                <li>
                    <code>ValueOf(FieldName)</code>{' '}
                    {getText('msg_5d84e20818f2')}
                </li>
                <li>{getText('msg_6e95f3192a03')}</li>
                <li>{getText('msg_7fa6042a3b14')}</li>
            </ul>
        ),
    },
];

/**
 * Inserts text at the current cursor position of a text input/textarea using
 * execCommand so the insertion is added to the field's native undo/redo stack,
 * falling back to a direct value set (which does not support undo) if unsupported.
 */
const insertTextAtCursor = (targetElement, text) => {
    const isTextField =
        targetElement?.tagName === 'INPUT' ||
        targetElement?.tagName === 'TEXTAREA';
    if (!isTextField) {
        return;
    }

    const start = targetElement.selectionStart ?? targetElement.value.length;
    const end = targetElement.selectionEnd ?? targetElement.value.length;

    targetElement.focus();
    targetElement.setSelectionRange(start, end);

    const insertedViaExecCommand = document.execCommand(
        'insertText',
        false,
        text,
    );
    if (insertedViaExecCommand) {
        return;
    }

    const newValue = `${targetElement.value.slice(0, start)}${text}${targetElement.value.slice(end)}`;

    const valueSetter = Object.getOwnPropertyDescriptor(
        targetElement.tagName === 'TEXTAREA'
            ? window.HTMLTextAreaElement.prototype
            : window.HTMLInputElement.prototype,
        'value',
    ).set;
    valueSetter.call(targetElement, newValue);
    targetElement.dispatchEvent(new Event('input', { bubbles: true }));

    const cursorPosition = start + text.length;
    targetElement.setSelectionRange(cursorPosition, cursorPosition);
};

const DefineMappingTab = ({
    templateData,
    setTemplateData,
    selectedTemplateType,
    setSelectedTemplateType,
}) => {
    if (!selectedTemplateType) {
        return null;
    }

    const [isFunctionsDrawerOpen, setIsFunctionsDrawerOpen] = useState(false);
    const lastFocusedFieldRef = useRef(null);

    const openFunctionsDrawer = () => {
        setIsFunctionsDrawerOpen(true);
    };

    const closeFunctionsDrawer = () => {
        setIsFunctionsDrawerOpen(false);
    };

    const handleFunctionsDrawerContentClick = event => {
        if (event.target.tagName !== 'CODE') {
            return;
        }
        insertTextAtCursor(
            lastFocusedFieldRef.current,
            event.target.textContent,
        );
        closeFunctionsDrawer();
    };

    const handleInputChange = (event, index) => {
        const { name, value } = event.target;
        const updatedGroups = [
            ...(selectedTemplateType?.importMapGroups || []),
        ];
        updatedGroups[index] = {
            ...updatedGroups[index],
            [name]: value,
        };
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const { hasSecurityAttribute } = useAuthContext();

    const hasFullAccess = hasSecurityAttribute([
        SecurityAttributes.CustomImportsFullAccess,
    ]);

    const { showModal } = useContext(ModalContext);

    const handleCheckboxChange = (event, index) => {
        const { checked } = event.target;
        const updatedGroups = [
            ...(selectedTemplateType?.importMapGroups || []),
        ];
        updatedGroups[index] = {
            ...updatedGroups[index],
            selected: checked,
        };
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleAddTranslationMap = () => {
        const updatedGroups = [
            ...(selectedTemplateType?.importMapGroups || []),
        ];
        updatedGroups.push({
            id: 0,
            importTemplateId: templateData.id,
            importTemplateTypeId: selectedTemplateType?.id || 0,
            importMapGroupTypeId: 1,
            processingOrder: updatedGroups.length + 1,
            errorMessage: '',
            warningMessage: '',
            importFieldId: null,
            translationMaps: [],
            formulaMap: null,
        });
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleAddFormulaMap = () => {
        const updatedGroups = [
            ...(selectedTemplateType?.importMapGroups || []),
        ];
        updatedGroups.push({
            id: 0,
            importTemplateId: templateData.id,
            importTemplateTypeId: selectedTemplateType?.id || 0,
            importMapGroupTypeId: 2,
            processingOrder: updatedGroups.length + 1,
            errorMessage: '',
            warningMessage: '',
            importFieldId: null,
            translationMaps: [],
            formulaMap: {
                id: 0,
                value: '',
                evaluations: [],
            },
        });
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleDeleteSelectedMaps = () => {
        const selectedMaps = (
            selectedTemplateType?.importMapGroups || []
        ).filter(group => group.selected);
        if (selectedMaps.length === 0) {
            Toast.error(getText('msg_74539c002d21'));
            return;
        }

        const updatedGroups = (
            selectedTemplateType?.importMapGroups || []
        ).filter(group => !group.selected);
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleDuplicateSelectedMaps = () => {
        const selectedMaps = (
            selectedTemplateType?.importMapGroups || []
        ).filter(group => group.selected);
        if (selectedMaps.length === 0) {
            Toast.error(getText('msg_74539c002d21'));
            return;
        }

        const duplicatedMaps = selectedMaps.map((group, index) => ({
            ...group,
            id: 0,
            selected: false,
            processingOrder:
                (selectedTemplateType?.importMapGroups || []).length +
                index +
                1,
            translationMaps: group.translationMaps.map(map => ({
                ...map,
                id: 0,
            })),
            ...(group?.formulaMap
                ? {
                      formulaMap: {
                          ...group.formulaMap,
                          id: 0,
                          evaluations:
                              group?.formulaMap?.evaluations?.map(map => {
                                  return {
                                      ...map,
                                      id: 0,
                                  };
                              }) || [],
                      },
                  }
                : null),
        }));

        const updatedGroups = [
            ...(selectedTemplateType?.importMapGroups || []),
            ...duplicatedMaps,
        ];
        setSelectedTemplateType(prevType => ({
            ...prevType,
            importMapGroups: updatedGroups,
        }));
    };

    const handleMoveMapUp = index => {
        if (index > 0) {
            const updatedGroups = [
                ...(selectedTemplateType?.importMapGroups || []),
            ];
            const temp = updatedGroups[index];
            updatedGroups[index] = updatedGroups[index - 1];
            updatedGroups[index - 1] = temp;

            updatedGroups.forEach((group, i) => {
                group.processingOrder = i + 1;
            });

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));
        }
    };

    const testMap = index => {
        const group = (selectedTemplateType?.importMapGroups || [])[index];

        showModal({
            type: ModalTypes.IMPORT_MAP_TEST,
            data: {
                formulaMap: group.formulaMap,
                errorMessage: group.errorMessage,
                warningMessage: group.warningMessage,
            },
        });
    };

    const handleMoveMapDown = index => {
        if (index < (selectedTemplateType?.importMapGroups || []).length - 1) {
            const updatedGroups = [
                ...(selectedTemplateType?.importMapGroups || []),
            ];
            const temp = updatedGroups[index];
            updatedGroups[index] = updatedGroups[index + 1];
            updatedGroups[index + 1] = temp;

            updatedGroups.forEach((group, i) => {
                group.processingOrder = i + 1;
            });

            setSelectedTemplateType(prevType => ({
                ...prevType,
                importMapGroups: updatedGroups,
            }));
        }
    };

    return (
        <div className={`${styles.DefineMappingTab} mt-5`}>
            <div className={`form-container p-3 ${styles.mappingForm}`}>
                <div className="d-flex justify-content-end mb-5">
                    <div className="float-end d-flex align-items-center">
                        {hasFullAccess && (
                            <>
                                <span
                                    type="button"
                                    onClick={() => handleDeleteSelectedMaps()}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-trash3 me-2" />
                                    {getText('msg_f6fdbe48dc54')}
                                </span>
                                <span
                                    type="button"
                                    onClick={() =>
                                        handleDuplicateSelectedMaps()
                                    }
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-copy me-2" />
                                    {getText('msg_972d57379db3')}
                                </span>
                            </>
                        )}
                        <span
                            type="button"
                            onMouseDown={() => {
                                lastFocusedFieldRef.current =
                                    document.activeElement;
                            }}
                            onClick={openFunctionsDrawer}
                            className="ing-text-buttons mx-3"
                        >
                            <i className="bi bi-question-circle me-2" />
                            {getText('msg_4be3b1e7e401')}
                        </span>
                    </div>
                </div>
                <div className="mb-3">
                    {selectedTemplateType?.importMapGroups?.length ? (
                        (selectedTemplateType.importMapGroups || []).map(
                            (group, index) => (
                                <React.Fragment key={`${group.id}-${index}`}>
                                    {index === 1 && (
                                        <hr className="d-lg-none d-md-block" />
                                    )}
                                    <div
                                        className={`col-12 gap-2 mb-2 d-lg-flex ${styles.segmentRow}`}
                                    >
                                        <div
                                            className={` align-content-around  ${styles.segmentInputItem}`}
                                        >
                                            <label
                                                className={` mb-2 ${
                                                    index !== 0 &&
                                                    'd-lg-none mt-4'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            ></label>
                                            <input
                                                type="checkbox"
                                                className="form-check-input mb-3"
                                                checked={
                                                    group.selected || false
                                                }
                                                onChange={event =>
                                                    handleCheckboxChange(
                                                        event,
                                                        index,
                                                    )
                                                }
                                            />
                                        </div>
                                        <div
                                            className={` ${styles.segmentInputItem}`}
                                        >
                                            <label
                                                className={` mb-2 ${
                                                    index !== 0 && 'd-lg-none'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            >
                                                {getText('msg_fece87f09e5a')}
                                            </label>
                                            <div
                                                className={`d-flex ${styles.mapTypeIcons}`}
                                            >
                                                <span
                                                    className="d-flex"
                                                    onClick={() => {
                                                        const updatedGroups = [
                                                            ...(selectedTemplateType?.importMapGroups ||
                                                                []),
                                                        ];
                                                        updatedGroups[index] = {
                                                            ...updatedGroups[
                                                                index
                                                            ],
                                                            isOpen: !updatedGroups[
                                                                index
                                                            ].isOpen,
                                                        };
                                                        setSelectedTemplateType(
                                                            prevType => ({
                                                                ...prevType,
                                                                importMapGroups:
                                                                    updatedGroups,
                                                            }),
                                                        );
                                                    }}
                                                >
                                                    {group.isOpen ? (
                                                        <i className="m-auto bi bi-chevron-down"></i>
                                                    ) : (
                                                        <i className="m-auto bi bi-chevron-up"></i>
                                                    )}
                                                </span>
                                                {group.importMapGroupTypeId ===
                                                1 ? (
                                                    <TranslateIcon />
                                                ) : (
                                                    <FormulaIcon />
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className={` col-12 col-lg-3  ${styles.segmentInputItem}`}
                                        >
                                            <label
                                                className={` mb-2 ${
                                                    index !== 0 && 'd-lg-none'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            >
                                                {getText('msg_d42713493ca8')}
                                            </label>
                                            <ImportFieldDropdown
                                                importTypeId={
                                                    selectedTemplateType?.importTypeId
                                                }
                                                selectedField={
                                                    group.importFieldId
                                                        ? {
                                                              id: group.importFieldId,
                                                              name: group.importFieldName,
                                                          }
                                                        : null
                                                }
                                                onFieldChange={selectedField => {
                                                    const updatedGroups = [
                                                        ...(selectedTemplateType?.importMapGroups ||
                                                            []),
                                                    ];
                                                    updatedGroups[index] = {
                                                        ...updatedGroups[index],
                                                        importFieldId:
                                                            selectedField?.id ||
                                                            null,
                                                        importFieldName:
                                                            selectedField?.name ||
                                                            '',
                                                    };
                                                    setSelectedTemplateType(
                                                        prevType => ({
                                                            ...prevType,
                                                            importMapGroups:
                                                                updatedGroups,
                                                        }),
                                                    );
                                                }}
                                                required
                                            />
                                        </div>
                                        <div
                                            className={` flex-fill ${styles.segmentInputItem}`}
                                        >
                                            <label
                                                className={` mb-2 ${
                                                    index !== 0 && 'd-lg-none'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            >
                                                {getText('msg_adf3b98e4dc1')}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={group.errorMessage || ''}
                                                onChange={event =>
                                                    handleInputChange(
                                                        event,
                                                        index,
                                                    )
                                                }
                                                name="errorMessage"
                                            />
                                        </div>
                                        <div
                                            className={` flex-fill ${styles.segmentInputItem}`}
                                        >
                                            <label
                                                className={` mb-2 ${
                                                    index !== 0 && 'd-lg-none'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            >
                                                {getText('msg_1269e7478c30')}
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={
                                                    group.warningMessage || ''
                                                }
                                                onChange={event =>
                                                    handleInputChange(
                                                        event,
                                                        index,
                                                    )
                                                }
                                                name="warningMessage"
                                            />
                                        </div>

                                        <div
                                            className={`align-content-center flex-fill`}
                                        >
                                            <label
                                                className={` mb-2 pt-4 ${
                                                    index !== 0 && 'd-lg-none'
                                                } d-md-block ${styles.segmentInputItem}`}
                                            ></label>
                                            {group.importMapGroupTypeId ===
                                                2 && (
                                                <i
                                                    className="bi bi-arrow-up-right-circle-fill cursor-pointer text-primary fs-4 pb-1"
                                                    onClick={() =>
                                                        testMap(index)
                                                    }
                                                >
                                                    {' '}
                                                </i>
                                            )}
                                            {index > 0 && (
                                                <button
                                                    className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                                    onClick={() =>
                                                        handleMoveMapUp(index)
                                                    }
                                                >
                                                    {getText(
                                                        'msg_2038bdec9210',
                                                    )}
                                                </button>
                                            )}
                                            {index !=
                                                selectedTemplateType
                                                    ?.importMapGroups?.length -
                                                    1 && (
                                                <button
                                                    className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                                    onClick={() =>
                                                        handleMoveMapDown(index)
                                                    }
                                                >
                                                    {getText(
                                                        'msg_bf93e5ce8bc1',
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {group.isOpen && (
                                        <TranslationMapsAccordion
                                            group={group}
                                            index={index}
                                            templateData={templateData}
                                            setTemplateData={setTemplateData}
                                            selectedTemplateType={
                                                selectedTemplateType
                                            }
                                            setSelectedTemplateType={
                                                setSelectedTemplateType
                                            }
                                        />
                                    )}
                                </React.Fragment>
                            ),
                        )
                    ) : (
                        <p className="text-center">
                            {getText('msg_ba23bb1da901')}
                        </p>
                    )}
                </div>
            </div>
            <div className="text-center align-items-center me-4 d-lg-block d-sm-none">
                <div className="mb-3">
                    <div className="col-sm-12 d-flex">
                        <button
                            type="button"
                            onClick={() => handleAddTranslationMap()}
                            className="ing-popup-blue-negative submit-button-form mx-3"
                        >
                            {getText('msg_184a224d1077')}
                        </button>

                        <button
                            type="button"
                            onClick={() => handleAddFormulaMap()}
                            className="ing-popup-blue-negative submit-button-form mx-3"
                        >
                            {getText('msg_6106c6990f44')}
                        </button>
                    </div>
                </div>
            </div>
            <SideDrawer
                isOpen={isFunctionsDrawerOpen}
                setIsOpen={setIsFunctionsDrawerOpen}
                header={getText('msg_4be3b1e7e401')}
                sections={MAPPER_FUNCTIONS_SECTIONS}
                onContentClick={handleFunctionsDrawerContentClick}
            />
        </div>
    );
};

DefineMappingTab.propTypes = {
    templateData: PropTypes.object.isRequired,
    setTemplateData: PropTypes.func.isRequired,
    selectedTemplateType: PropTypes.object,
    setSelectedTemplateType: PropTypes.func.isRequired,
};

export default DefineMappingTab;
